import {
  ActivityTypeCode,
  CouponTypeCode,
  UserLevel,
  Whether,
} from "project-config/constants";
import {
  BalanceTipType,
  ExtraWelfareTipType,
  LotteryActivityInfo,
  RechargeInfo,
} from "../types";
import { couponTypeTranslate } from "@/components/top-up/help/couponTypeTranslate";
import { getTranslateNsT } from "@/utils/getTranslateNsT";
import {
  AnimationVipDesc,
  BalanceWelfareType,
  ExtraWelfareType,
} from "../constants/animation-balance-info";
import {
  findWelfareData,
  getLotteryNumber,
  presentAmountCalculate,
  rechargeLevel,
} from "@/components/top-up/help/activityUtil";
import { initVerifyRechargeInfo } from "../store";

const t = getTranslateNsT("topup");

export class RechargeTips {
  private rechargeInfo: RechargeInfo = {
    amount: 0,
    userProperty: {
      isReRegister: 0,
      picRechargeOver: 0,
      isFirstRecharge: 0,
      isFocusOnWX: 0,
      isBindMiniPro: 0,
      isRealName: 0,
      animationInviteCount: 0,
    },
    userLevel: null,
    userCumulative: 0,
    currencySymbol: "",
    isAnimationActiveOverTime: false,
    HangTags: [],
    activityInfo: {
      id: 0,
      activityType: -1,
      activityName: "",
      activityDetail: [],
      activityNameEn: "",
    },
    luckDrawGiftActivityInfo: {
      id: 0,
      activityType: -1,
      activityName: "",
      activityDetail: [],
      activityNameEn: "",
    },
    lotteryActivityInfoList: [],
    checkedRadioId: 0,
    verifyRechargeInfo: initVerifyRechargeInfo,
  };
  public balanceTip: BalanceTipType[] = [
    {
      type: BalanceWelfareType.balance,
      content: "",
    },
    {
      type: BalanceWelfareType.UpGradeVip,
      content: "",
      pre: t("animation_upgrade_text"),
    },
  ];

  public extraWelfareTip: ExtraWelfareTipType[] = [
    {
      type: ExtraWelfareType.GPU,
      content: [],
      pre: couponTypeTranslate(ExtraWelfareType.GPU),
      contentPre: "￥",
    },
    {
      type: ExtraWelfareType.CPU,
      content: [],
      pre: couponTypeTranslate(ExtraWelfareType.CPU),
      contentPre: "￥",
    },
    {
      type: ExtraWelfareType.Universal,
      content: [],
      pre: couponTypeTranslate(ExtraWelfareType.Universal),
      contentPre: "￥",
    },
    {
      type: ExtraWelfareType.CPUQingCloud,
      content: [],
      pre: couponTypeTranslate(ExtraWelfareType.CPUQingCloud),
      contentPre: "￥",
    },
    {
      type: ExtraWelfareType.GPUQingCloud,
      content: [],
      pre: couponTypeTranslate(ExtraWelfareType.GPUQingCloud),
      contentPre: "￥",
    },
    {
      type: ExtraWelfareType.LuckyDrawGift,
      content: [],
      pre: t("topup_lottery_coupon"),
      contentSuf: t("topup_lottery_coupon_unit"),
    },
    {
      type: ExtraWelfareType.Desktop,
      content: [],
      pre: couponTypeTranslate(ExtraWelfareType.Desktop),
      contentPre: "￥",
    },
  ];

  private balance = this.balanceTip.find(
    (item) => item.type === BalanceWelfareType.balance
  );
  private upgradeVip = this.balanceTip.find(
    (item) => item.type === BalanceWelfareType.UpGradeVip
  );
  private luckyDraw = this.extraWelfareTip.find(
    (item) => item.type === ExtraWelfareType.LuckyDrawGift
  );
  private cpuQingCloud = this.extraWelfareTip.find(
    (item) => item.type === ExtraWelfareType.CPUQingCloud
  );
  private universal = this.extraWelfareTip.find(
    (item) => item.type === ExtraWelfareType.Universal
  );
  private desktop = this.extraWelfareTip.find(
    (item) => item.type === ExtraWelfareType.Desktop
  );

  constructor(rechargeInfo: RechargeInfo) {
    this.rechargeInfo = rechargeInfo;
  }

  public init() {
    this.rechargeAmount();
    this.LuckyDrawGiftTips();
    this.qingCloudTips();
    this.rechargeGiftCouponTips();
    this.newUserTips();
    this.desktopTips();
    this.rechargeExchange();
  }

  // 余额到账(包含余额+升级会员)
  private rechargeAmount() {
    const {
      amount,
      currencySymbol,
      userLevel,
      activityInfo,
      userCumulative,
      HangTags,
    } = this.rechargeInfo;
    if (
      activityInfo &&
      activityInfo.activityType === ActivityTypeCode.QingCloudGift
    )
      return;
    if (this.balance) {
      this.balance.content = `${currencySymbol}${amount}`;
    }
    if (
      !activityInfo &&
      userLevel &&
      userLevel !== UserLevel.Diamond &&
      HangTags &&
      this.upgradeVip
    ) {
      const riseLevel =
        rechargeLevel(HangTags, true, userCumulative, amount) ||
        UserLevel.Normal;
      if (riseLevel > userLevel) {
        this.upgradeVip.content = `${t(AnimationVipDesc[riseLevel])}`;
      }
    }
  }

  // 抽奖券活动
  private LuckyDrawGiftTips() {
    const { luckDrawGiftActivityInfo, amount, lotteryActivityInfoList } =
      this.rechargeInfo;
    if (
      luckDrawGiftActivityInfo &&
      luckDrawGiftActivityInfo.activityType === ActivityTypeCode.LuckyDrawGift
    ) {
      const num = getLotteryNumber(
        luckDrawGiftActivityInfo,
        lotteryActivityInfoList as LotteryActivityInfo[],
        amount
      );
      if (num > 0 && this.luckyDraw) {
        this.luckyDraw.content.push(num);
      }
    }
  }

  // 新用户福利（15天内新注册用户单笔满100送25通用券）
  private newUserTips() {
    const { amount, activityInfo, userProperty } = this.rechargeInfo;
    if (
      amount <= 0 ||
      activityInfo?.activityType === ActivityTypeCode.GiftMember ||
      activityInfo?.activityType === ActivityTypeCode.QingCloudGift
    )
      return;
    if (
      userProperty.animationNewUserRegister === Whether.No &&
      userProperty.animationInviteCount === 0 &&
      amount >= 50
    ) {
      if (this.universal) {
        this.universal.content.push(20);
      }
    }
  }

  // 青云活动(CPU青云 2000~4999/0.28125，5000及以上按卡片赠送)
  private qingCloudTips() {
    const { activityInfo, amount, currencySymbol } = this.rechargeInfo;
    if (activityInfo?.activityType === ActivityTypeCode.QingCloudGift) {
      if (
        activityInfo.activityDetail?.[0]?.couponType ===
          CouponTypeCode.CPUQingCloud &&
        amount &&
        amount >= 2000 &&
        amount <= 4999 &&
        this.cpuQingCloud
      ) {
        this.cpuQingCloud.content.push(
          `${t("activity_qing_cloud_arrive")} ${currencySymbol}${Math.floor(
            amount / 0.28125
          )}`
        );
        return;
      }
      const detail = activityInfo.activityDetail.find(
        (item) => item.amount === amount
      );
      const extra = this.extraWelfareTip.find(
        (item) => item.type === detail?.couponType
      );
      if (extra && detail) {
        extra.content.push(` ${currencySymbol}${detail.presentAmount}`);
      }
    }
  }

  // 云制作币活动
  private desktopTips() {
    const { activityInfo, amount, currencySymbol } = this.rechargeInfo;
    if (activityInfo?.activityType === ActivityTypeCode.Desktop) {
      const detail = activityInfo.activityDetail.find(
        (item) => item.amount === amount
      );
      if (this.desktop) {
        if (detail) {
          this.desktop.content.push(
            ` ${currencySymbol}${detail.presentAmount}`
          );
        } else {
          this.desktop.content.push(` ${currencySymbol}${amount}`);
        }
      }
    }
  }

  // 充值赠送券活动
  private rechargeGiftCouponTips() {
    const {
      activityInfo,
      amount,
      checkedRadioId,
      userCumulative,
      HangTags,
      userLevel,
    } = this.rechargeInfo;
    if (amount < 1) return;
    if (activityInfo?.activityType === ActivityTypeCode.RechargeGift) {
      const welfareData = findWelfareData(activityInfo, amount);
      if (welfareData) {
        let welfare = welfareData;
        if (welfare.detailList && welfare.detailList.length > 1) {
          welfare =
            welfare.detailList.find((item) => item.id === checkedRadioId) || {};
        }
        const calcWelfareData = presentAmountCalculate(
          activityInfo,
          amount,
          welfare.presentAmount
        );
        const welfareCouponType = welfare.couponType;
        const extra = this.extraWelfareTip.find(
          (item) => item.type === welfareCouponType
        );
        if (extra) {
          extra.content.push(calcWelfareData);
        }
      }
      if (
        userLevel &&
        userLevel !== UserLevel.Diamond &&
        HangTags &&
        this.upgradeVip
      ) {
        const riseLevel =
          rechargeLevel(HangTags, true, userCumulative, amount) ||
          UserLevel.Normal;
        if (riseLevel > userLevel) {
          this.upgradeVip.content = `${t(AnimationVipDesc[riseLevel])}`;
        }
      }
    }
  }

  // animation充值兑换
  private rechargeExchange() {
    const { amount, verifyRechargeInfo } = this.rechargeInfo;
    if (!verifyRechargeInfo) return;
    const { couponNO, rechargeAmount, couponAmount, couponType } =
      verifyRechargeInfo;
    const extra = this.extraWelfareTip.find((item) => item.type === couponType);
    if (couponNO && amount >= rechargeAmount && this.universal && extra) {
      extra.content.push(couponAmount);
    }
  }
}
